import { useRef, useEffect, memo, useCallback } from 'react'

import * as Styled from './styled'

import { useTimelineContext } from 'context/TimelineContext'
import { EVENT_TYPES, eventBus } from 'services/event-bus'

const PlayHint = () => {
  const playHintRef = useRef<HTMLDivElement>(null)
  const {
    playHeadClickableAreaRef,
    playHeadRef,
    getCurrentTimeByOffsetLeft,
    setPlayHeadCurrentTime,
    onPause,
    resumePlaying
  } = useTimelineContext()

  const handleMouseEnter = useCallback(() => {
    if (playHintRef.current) {
      playHintRef.current.style.display = 'block'
    }
  }, [playHintRef])

  const handleMouseLeave = useCallback(() => {
    if (playHintRef.current) {
      playHintRef.current.style.display = 'none'
    }
  }, [playHintRef])

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (
        !playHeadClickableAreaRef.current ||
        !playHintRef.current ||
        !playHeadRef.current
      )
        return

      const playHeadAreaLeft =
        playHeadClickableAreaRef.current.getBoundingClientRect().left
      const actualX = Math.max(0, event.clientX - playHeadAreaLeft)

      const isOverPlayHead = playHeadRef.current.contains(event.target as Node)

      playHintRef.current.style.left = `${actualX}px`
      playHintRef.current.style.display = isOverPlayHead ? 'none' : 'block'
    },
    [playHeadClickableAreaRef, playHintRef, playHeadRef]
  )

  const handlePlayHeadAreaClick = useCallback(
    (e: MouseEvent) => {
      if (!playHeadClickableAreaRef.current) return
      // if timeline is playing, pause it to not mess the timeline time
      onPause()
      const playHeadAreaLeft =
        playHeadClickableAreaRef.current.getBoundingClientRect().left
      const clickX = Math.max(0, e.clientX - playHeadAreaLeft)
      const currentTime = getCurrentTimeByOffsetLeft(clickX)

      setPlayHeadCurrentTime(currentTime)
      eventBus.publish(EVENT_TYPES.PLAYBACK_TIME_CHANGE, currentTime)
      // if timeline was playing, resume it
      resumePlaying()
    },
    [
      getCurrentTimeByOffsetLeft,
      setPlayHeadCurrentTime,
      playHeadClickableAreaRef,
      onPause,
      resumePlaying
    ]
  )

  useEffect(() => {
    const playHeadAreaEl = playHeadClickableAreaRef.current
    if (!playHeadAreaEl) return

    playHeadAreaEl.addEventListener('mousemove', handleMouseMove)
    playHeadAreaEl.addEventListener('mouseleave', handleMouseLeave)
    playHeadAreaEl.addEventListener('mouseenter', handleMouseEnter)
    playHeadAreaEl.addEventListener('click', handlePlayHeadAreaClick)

    return () => {
      playHeadAreaEl.removeEventListener('mousemove', handleMouseMove)
      playHeadAreaEl.removeEventListener('mouseleave', handleMouseLeave)
      playHeadAreaEl.removeEventListener('mouseenter', handleMouseEnter)
      playHeadAreaEl.removeEventListener('click', handlePlayHeadAreaClick)
    }
  }, [
    playHeadClickableAreaRef,
    handlePlayHeadAreaClick,
    handleMouseMove,
    handleMouseLeave,
    handleMouseEnter
  ])

  return <Styled.PlayHint ref={playHintRef} />
}

export default memo(PlayHint)
